import React, { useEffect, useState } from 'react'
import { Alert, Box, Button, Container, Link, Paper, Stack, TextField, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { submitContactRequest } from '../service'
import { CONTACT_REASONS, TERMS_PATH } from '../constants/legal'

const emptyForm = { reason: '', name: '', email: '', contentLink: '', message: '', website: '' }

// Public page: the site has no email address, so takedown, removal and deletion requests and
// account questions come through this form. It works without signing in.
export default function Contact () {
  const [form, setForm] = useState(emptyForm)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [sent, setSent] = useState(false)

  useEffect(() => {
    const previous = document.title
    document.title = 'Contact · Highlights'
    return () => { document.title = previous }
  }, [])

  const update = field => event => setForm(current => ({ ...current, [field]: event.target.value }))

  const submit = async event => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await submitContactRequest(form)
      setSent(true)
      setForm(emptyForm)
    } catch (err) {
      setError(err.message || 'Your message was not sent. Please try again in a moment.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 3, md: 6 } }}>
      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 } }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="overline" color="text.secondary">Highlights</Typography>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
              Contact the site owner
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              Report content or a copyright problem, ask for a video of you or your child to be
              removed, have your videos or account deleted, or get help with your account. You do
              not need an account. The site owner reviews each message and may reply to the email
              address you give.
            </Typography>
          </Box>

          {sent
            ? <Stack spacing={2}>
                <Alert severity="success">Thanks. Your message was received.</Alert>
                <Box>
                  <Button variant="outlined" onClick={() => setSent(false)}>Send another message</Button>
                </Box>
              </Stack>
            : <Box component="form" onSubmit={submit}>
                <Stack spacing={2.25}>
                  <TextField
                    select
                    label="Reason"
                    value={form.reason}
                    onChange={update('reason')}
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                    disabled={busy}
                    required
                    fullWidth
                  >
                    <option value="" disabled>Choose a reason</option>
                    {CONTACT_REASONS.map(reason => (
                      <option key={reason.value} value={reason.value}>{reason.label}</option>
                    ))}
                  </TextField>
                  <TextField
                    label="Your name (optional)"
                    value={form.name}
                    onChange={update('name')}
                    autoComplete="name"
                    inputProps={{ maxLength: 200 }}
                    disabled={busy}
                    fullWidth
                  />
                  <TextField
                    label="Your email"
                    type="email"
                    value={form.email}
                    onChange={update('email')}
                    autoComplete="email"
                    inputProps={{ maxLength: 320 }}
                    disabled={busy}
                    required
                    fullWidth
                  />
                  <TextField
                    label="Link to the content (optional)"
                    value={form.contentLink}
                    onChange={update('contentLink')}
                    helperText="The link to the video, highlight or share link, if you have one."
                    inputProps={{ maxLength: 2000 }}
                    disabled={busy}
                    fullWidth
                  />
                  <TextField
                    label="Message"
                    value={form.message}
                    onChange={update('message')}
                    helperText="Describe the content and where it appears, or what you need. At least 10 characters."
                    inputProps={{ minLength: 10, maxLength: 5000 }}
                    disabled={busy}
                    required
                    multiline
                    minRows={5}
                    fullWidth
                  />
                  {/* Honeypot: hidden from people and screen readers; automated senders fill it in. */}
                  <Box
                    aria-hidden="true"
                    sx={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}
                  >
                    <label htmlFor="contact-website">Website</label>
                    <input
                      id="contact-website"
                      name="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={update('website')}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Your message, email address and IP address are stored so the site owner can
                    respond and prevent abuse. Do not include passwords.
                  </Typography>
                  {error ? <Alert severity="error">{error}</Alert> : null}
                  <Box>
                    <Button type="submit" variant="contained" size="large" disabled={busy}>
                      {busy ? 'Sending...' : 'Send message'}
                    </Button>
                  </Box>
                </Stack>
              </Box>}

          <Typography variant="body2" color="text.secondary">
            <Link component={RouterLink} to={TERMS_PATH}>Terms of Use</Link>
            {' · '}
            <Link component={RouterLink} to="/">Go to Highlights</Link>
          </Typography>
        </Stack>
      </Paper>
    </Container>
  )
}
